import React, { useState } from 'react';
import { SignedIn, SignedOut, SignIn, SignUp, UserButton, useAuth, useUser } from '@clerk/clerk-react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import GtWelcomeChooser from './screens/gt/GtWelcomeChooser';
import GtCandidatesTable from './screens/gt/GtCandidatesTable';
import GtRolesTable from './screens/gt/GtRolesTable';
import GtClientsTable from './screens/gt/GtClientsTable';
import GtBenches from './screens/gt/GtBenches';
import wordmarkBlack from './brand/main-logo-black.png'; // C1-LOGO
import brandMark from './brand/favicon-black.png'; // C1-LOGO2: the G mark in the rail
import './components/gt.css';
import GtPublicShare from './screens/gt/GtPublicShare';
import GtApply from './screens/gt/GtApply';
import GtPortal from './screens/gt/GtPortal';
import GtInterviewRoom from './screens/gt/GtInterviewRoom';
import GtLexaRoom from './screens/gt/GtLexaRoom'; // C1-LEXA2
import GtLexaAdmin, { LexaSwitch } from './screens/gt/GtLexaAdmin';
import GtBethAdmin from './screens/gt/GtBethAdmin'; // C1-BETH8
import GtInterviewsPanel from './components/GtInterviewsPanel';
import GtHome from './screens/gt/GtHome';
import API_BASE from './config';
import { NAV_PATHS, navItemForPath } from './components/gtRoutes';
import GtNotifyBell from './components/GtNotifyBell';
import GtSearchPalette from './components/GtSearchPalette'; // C1-SEARCH
import GtForms from './screens/gt/GtForms';
import GtSequences from './screens/gt/GtSequences'; // C1-SEQ2
import GtJobFamilies from './screens/gt/GtJobFamilies'; // C1-JOBFAM2
import GtInsights from './screens/gt/GtInsights'; // C1-INSIGHTS2
import GtOutreach from './screens/gt/GtOutreach'; // C1-OUTREACH
import GtSettings from './screens/gt/GtSettings'; // C1-USAGE
import GtPublicForm from './screens/gt/GtPublicForm';
import { getImpersonation, clearImpersonation, installImpersonationFetch } from './impersonation';
import { captureLexaIntent, readLexaIntent } from './screens/gt/portal/portalKit'; // P2-PORTAL

installImpersonationFetch();

// Shown across the top of the portal for the whole session. Staff forgetting
// they are inside someone else's account is the risk this exists to prevent.
function ImpersonationBanner({ grant, onExit }) {
  const [busy, setBusy] = useState(false);
  const { getToken } = useAuth();
  const end = async () => {
    setBusy(true);
    try {
      const token = await getToken();
      await fetch(API_BASE + '/api/impersonate/end', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ token: grant.token }),
      });
    } catch (e) { /* the grant expires on its own regardless */ }
    clearImpersonation();
    onExit();
  };
  const who = (grant.candidate && grant.candidate.full_name) || 'this candidate';
  return (
    <div className="gt-impbanner">
      <span className="gt-impbanner-txt">
        You are viewing the portal as <b>{who}</b>. Everything you change is saved to their account.
      </span>
      <button className="gt-impbanner-exit" onClick={end} disabled={busy}>
        {busy ? 'Leaving\u2026' : 'Exit candidate view'}
      </button>
    </div>
  );
}

const NAV_ICONS = {
  Home: 'M3 10.5 12 3l9 7.5 M5.5 9.5V20a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V9.5 M9.5 21v-6h5v6',
  Roles: 'M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2 M4 7h16a1 1 0 0 1 1 1v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a1 1 0 0 1 1-1z M3 12h18',
  Benches: 'M12 3 3 8l9 5 9-5-9-5z M3 12.5l9 5 9-5 M3 17l9 5 9-5',
  Clients: 'M3 21h18 M5 21V7l8-4v18 M19 21V11l-6-3 M9 9v.01 M9 12v.01 M9 15v.01 M9 18v.01',
  Candidates: 'M16 19v-1a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v1 M9.5 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z M21 19v-1a4 4 0 0 0-3-3.87 M15.5 4.13a3.5 3.5 0 0 1 0 6.74',
  Intake: 'M12 3v10 M8 9l4 4 4-4 M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3',
  Matches: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 16.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9z M12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2z',
  Interviews: 'M21 12a8 8 0 0 1-8 8H4l2.5-2.5A8 8 0 1 1 21 12z M8.5 12h.01 M12 12h.01 M15.5 12h.01',
  Forms: 'M9 3h6a1 1 0 0 1 1 1v1H8V4a1 1 0 0 1 1-1z M8 5H6a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2 M8 11h8 M8 15h5',
  Sequences: 'M22 2 11 13 M22 2l-7 20-4-9-9-4 20-7z',
  'Job families': 'M4 4h6.5v6.5H4z M13.5 4H20v6.5h-6.5z M4 13.5h6.5V20H4z M13.5 13.5H20V20h-6.5z',
  Insights: 'M5 3h10l4 4v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z M14 3v5h5 M8 12h8 M8 16h8 M8 8h3',
  'Auto outreach': 'M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1z M15.5 8.5a5 5 0 0 1 0 7 M18.5 5.5a9 9 0 0 1 0 13', // C1-OUTREACH
  Inbox: 'M22 12h-6l-2 3h-4l-2-3H2 M5 5h14a1 1 0 0 1 1 1v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a1 1 0 0 1 1-1z',
  Delivery: 'M21 8l-9-5-9 5v8l9 5 9-5V8z M3.3 8.5 12 13l8.7-4.5 M12 13v9',
  Settings: 'M4 21v-7 M4 10V3 M12 21v-9 M12 8V3 M20 21v-5 M20 12V3 M1.5 14h5 M9.5 8h5 M17.5 16h5',
};
// C1-NAV41: Intake, Matches and Inbox are off the menu (26 Oct). Their routes stay.
const NAV = [
  'Home', 'Roles', 'Benches', 'Clients', 'Candidates', 'Interviews', 'Forms',
  'Sequences', 'Job families', 'Insights', 'Auto outreach', 'Delivery', 'Settings', // C1-OUTREACH
];
function NavIcon({ name }) {
  return (
    <svg className="gt-nav-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {NAV_ICONS[name].split(' M').map((d, i) => <path key={i} d={(i ? 'M' : '') + d} />)}
    </svg>
  );
}

const clerkAppearance = {
  variables: {
    colorPrimary: '#1FA9B2',
    borderRadius: '10px',
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  },
  layout: {
    logoPlacement: 'none',
    socialButtonsVariant: 'blockButton',
  },
  elements: {
    headerTitle: { display: 'none' },
    headerSubtitle: { display: 'none' },
    footer: { display: 'none' },
    rootBox: { width: '100%', maxWidth: '560px' },
    card: { width: '100%', maxWidth: '560px', padding: '2.5rem 3rem', boxSizing: 'border-box' },
    formFieldInput: { padding: '0.7rem 0.9rem', fontSize: '0.95rem' },
    formButtonPrimary: { padding: '0.7rem', fontSize: '0.95rem' },
    socialButtonsBlockButton: { padding: '0.65rem' },
  },
};

function Placeholder({ name }) {
  return (
    <div className="gt-work">
      <div className="gt-work-inner">
        <div className="gt-card">
          <div className="gt-empty">
            <div className="gt-empty-title">{name}</div>
            <div className="gt-empty-sub">
              This screen arrives with its build step. Nothing is wired here yet.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlaceholderScreen({ name }) {
  return (
    <>
      <div className="gt-topbar"><h1>{name}</h1></div>
      <Placeholder name={name} />
    </>
  );
}

function Shell() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  const userName = user?.fullName || user?.primaryEmailAddress?.emailAddress || null;
  const userImage = user?.imageUrl || null;
  const active = navItemForPath(location.pathname);
  // C1-SEARCH: search everything. Cmd+K (Mac) or Ctrl+K opens it anywhere.
  const [searchOpen, setSearchOpen] = useState(false);
  React.useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && String(e.key).toLowerCase() === 'k') { e.preventDefault(); setSearchOpen(true); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="gt-root">
      <nav className="gt-nav">
        <div className="gt-brand">
          <img src={brandMark} alt="Generative Talent Co." className="gt-brand-mark" />
        </div>
        <button className="gt-nav-item gt-nav-search" onClick={() => setSearchOpen(true)} title="Search everything (Cmd+K)">
          <svg className="gt-nav-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z" /><path d="M21 21l-4.3-4.3" /></svg>
          Search
        </button>
        {NAV.map((n) => (
          <button
            key={n}
            className={'gt-nav-item' + (active === n ? ' gt-nav-item--active' : '')}
            onClick={() => navigate(NAV_PATHS[n])}
          >
            <NavIcon name={n} />
            {n}
          </button>
        ))}
        <GtNotifyBell getToken={getToken} navigate={navigate} />
        <div className="gt-nav-foot">
          <UserButton />
        </div>
      </nav>
      <main className="gt-main">
        <Routes>
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="/home" element={<GtHome getToken={getToken} />} />

          <Route path="/candidates" element={<GtCandidatesTable getToken={getToken} userName={userName} userImage={userImage} />} />
          <Route path="/candidates/:candidateId" element={<GtCandidatesTable getToken={getToken} userName={userName} userImage={userImage} />} />

          <Route path="/roles" element={<GtRolesTable getToken={getToken} userName={userName} userImage={userImage} />} />
          <Route path="/roles/:roleId" element={<GtRolesTable getToken={getToken} userName={userName} userImage={userImage} />} />
          <Route path="/roles/:roleId/candidates/:candidateId" element={<GtRolesTable getToken={getToken} userName={userName} userImage={userImage} />} />
          <Route path="/benches" element={<GtBenches getToken={getToken} userName={userName} userImage={userImage} />} />
          <Route path="/benches/:benchId" element={<GtBenches getToken={getToken} userName={userName} userImage={userImage} />} />
          <Route path="/benches/:benchId/candidates/:candidateId" element={<GtBenches getToken={getToken} userName={userName} userImage={userImage} />} />

          <Route path="/clients" element={<GtClientsTable getToken={getToken} userName={userName} userImage={userImage} />} />
          <Route path="/clients/:clientId" element={<GtClientsTable getToken={getToken} userName={userName} userImage={userImage} />} />
          <Route path="/clients/:clientId/roles/:roleId" element={<GtClientsTable getToken={getToken} userName={userName} userImage={userImage} />} />
          <Route path="/clients/:clientId/roles/:roleId/candidates/:candidateId" element={<GtClientsTable getToken={getToken} userName={userName} userImage={userImage} />} />

          <Route path="/intake" element={<PlaceholderScreen name="Intake" />} />
          <Route path="/matches" element={<PlaceholderScreen name="Matches" />} />
          <Route path="/interviews" element={<div className="gtc-wrap gtivs"><div className="gtc-head"><div><h1 className="gtc-title">Interviews</h1><p className="gtc-sub">Every interview with Nathan. Scored by AI, decided by a person.</p></div></div><LexaSwitch active="nathan" /><GtInterviewsPanel kind="all" ownerId="all" getToken={getToken} /></div>} />
          <Route path="/interviews/lexa" element={<GtLexaAdmin getToken={getToken} />} />
          <Route path="/interviews/beth" element={<GtBethAdmin getToken={getToken} />} />{/* C1-BETH8 */}
          <Route path="/forms" element={<GtForms getToken={getToken} />} />
          <Route path="/forms/:formId" element={<GtForms getToken={getToken} />} />
          <Route path="/sequences" element={<GtSequences getToken={getToken} />} />{/* C1-SEQ2 */}
          <Route path="/sequences/:runId" element={<GtSequences getToken={getToken} />} />
          <Route path="/job-families" element={<GtJobFamilies getToken={getToken} />} />{/* C1-JOBFAM2 */}
          <Route path="/job-families/:familyId" element={<GtJobFamilies getToken={getToken} />} />
          <Route path="/insights" element={<GtInsights getToken={getToken} />} />{/* C1-INSIGHTS2 */}
          <Route path="/insights/:postId" element={<GtInsights getToken={getToken} />} />
          <Route path="/outreach" element={<GtOutreach getToken={getToken} />} />{/* C1-OUTREACH */}
          <Route path="/outreach/:tab" element={<GtOutreach getToken={getToken} />} />{/* C1-PEOPLE */}
          <Route path="/inbox" element={<PlaceholderScreen name="Inbox" />} />
          <Route path="/delivery" element={<PlaceholderScreen name="Delivery" />} />
          <Route path="/settings" element={<GtSettings getToken={getToken} />} />{/* C1-USAGE */}

          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </main>
      <GtSearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} getToken={getToken} navigate={navigate} />
    </div>
  );
}

function RoleGate() {
  const { user, isLoaded } = useUser();
  if (!isLoaded) return null;
  const role = user?.publicMetadata?.role;
  // An active grant wins: staff see the candidate's portal, not their own app.
  const grant = getImpersonation();
  if (grant) {
    return (
      <div className="gt-impwrap">
        <ImpersonationBanner grant={grant} onExit={() => window.location.assign('/candidates')} />
        <GtPortal />
      </div>
    );
  }
  // No role yet. Ask what they came for rather than making them wait for an
  // approval that never comes.
  if (!role) return <GtWelcomeChooser />;
  // Candidates get their own walled space. The internal app and its nav must
  // never render for a candidate, so this branch happens before Shell.
  if (role === 'candidate') return <GtPortal />;
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}

// P2-PORTAL: arrived from "practise for this role" on a job page.
function isLexaPrep() {
  try { if (new URLSearchParams(window.location.search).get('lexa') === 'prep') return true; } catch (e) { /* fine */ }
  return !!readLexaIntent();
}

export default function App() {
  const [wantsSignUp, setWantsSignUp] = useState(() => isLexaPrep()); // P2-PORTAL
  captureLexaIntent(); // P2-PORTAL: a job page's "practise for this role" survives sign-up

  // Public, unauthenticated route: /s/:token is the client-facing share page.
  // This must return BEFORE any Clerk component renders, and before the router
  // mounts, so the public page never touches auth or app routing.
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  if (path.startsWith('/s/')) {
    const shareToken = path.slice(3).split('/')[0].split('?')[0];
    if (shareToken) return <GtPublicShare token={shareToken} />;
  }

  // Public interview with Nathan: /interview/:token. The link is the key, so
  // it sits outside sign-in like the share page.
  if (path.startsWith('/interview/')) {
    const ivToken = path.slice(11).split('/')[0].split('?')[0];
    if (ivToken) return <GtInterviewRoom token={ivToken} />;
  }

  // C1-LEXA2: a coaching session with Lexa, /lexa/:token. The link is the key,
  // like Nathan's interview room, so a staff test call needs no candidate account.
  if (path.startsWith('/lexa/')) {
    const lxToken = path.slice(6).split('/')[0].split('?')[0];
    if (lxToken) return <GtLexaRoom token={lxToken} />;
  }

  // Public form: /f/:token. A person's own link or a form's shared link.
  if (path.startsWith('/f/')) {
    const formToken = path.slice(3).split('/')[0].split('?')[0];
    if (formToken) return <GtPublicForm token={formToken} />;
  }

  // Public apply entry point. GtApply renders its own Clerk auth (sign in/up and
  // return to this exact role), so it sits outside the internal SignedIn gate.
  if (path.startsWith('/apply/')) {
    return <GtApply clerkAppearance={clerkAppearance} />;
  }

  return (
    <>
      <SignedOut>
        <div className="gt-signin">
          <div className="gt-signin-box">
            <img src={wordmarkBlack} alt="Generative Talent Co" className="gt-signin-wordmark" />
            <p className="gt-signin-tagline">We recruit for what's next</p>
            {isLexaPrep() && <p className="gtap-applying-for">Create your account to practise with <strong>Lexa</strong>, our AI interview coach</p>}
            {wantsSignUp ? (
              <SignUp routing="hash" appearance={clerkAppearance} />
            ) : (
              <SignIn routing="hash" appearance={clerkAppearance} />
            )}
            <p className="gt-signin-toggle">
              {wantsSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button type="button" onClick={() => setWantsSignUp(!wantsSignUp)}>
                {wantsSignUp ? 'Sign in' : 'Sign up'}
              </button>
            </p>
          </div>
        </div>
      </SignedOut>
      <SignedIn>
        <RoleGate />
      </SignedIn>
    </>
  );
}
